import React, { useEffect } from 'react';
import { Layout } from '@/components/Common';
import CustomHead from '@/components/Common/Layout/Head';
import { NextPageWithLayout } from '@/pages/_app';
import type { GetServerSidePropsContext, InferGetStaticPropsType } from 'next';
import Script from 'next/script';
import { WEBSITE_URL } from '@/shared/constants';
import { businessSettingsDisplay, logPageView } from '@/lib/service';
import { BusinessSettingConfigKey, PublicSourcingMarketplaceCategoryPage } from '@/shared/types/business-setting';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import {
  AverageReviewFilterOptions,
  ISourcingFilterOption,
  MoqFilterOptions,
  ProductionTimeFilterOptions,
  SamplePriceFilterOptions,
  ViewType,
} from '@/shared/types/sourcing-marketplace';
import { getMatchingCategoryAndVertical, getVerticalName } from '@/shared/utils/sourcing-marketplace-util';
import { ParamKey } from '@/shared/types/url-params';
import BrowseSourcingLayout from '@/components/Common/Layout/BrowseSourcingLayout';
import {
  categoryCountBySuppliersInSearch,
  getCatalogCategories,
  getCatalogCompanies,
  getCatalogCompaniesByIds,
  supplierRecommendItemsToUser,
} from '@/lib/service/catalog';
import { SUPPLIERS_MAX_PAGE_SIZE, SUPPLIERS_PAGE_SIZE } from '@/shared/constants/sourcing-marketplace';
import { CatalogCategory, CatalogCompany, SearchRegion, SupplierSortKey } from '@/shared/types/catalog';
import BrowseSupplierList from '@/components/SourcingMarketplace/BrowseSourcing/BrowseSupplierList';
import { getBreadcrumbListJsonLd } from '@/shared/seo/jsonld';
import cloudfrontLoader from '@/cloudfront-loader';
import { delObjectKey, isMobileByUserAgent } from '@/shared/utils/utils';
import { forEach, isArray, isEmpty, isNil, keys, sampleSize, values } from 'lodash-es';

// #region - tool methods
// Get active checked L3 category ids, used as api params
const getActiveL3Ids = (context: GetServerSidePropsContext, L3CatgegoryList: CatalogCategory[]): number[] => {
  const L2CategoryKeyFromUrl = context.params.category as string;
  const vertical = context.query?.[ParamKey.L2_VERTICAL] as string;
  const verticalName = context.query?.[ParamKey.L2_VERTICAL_NAME] as string;
  let activeL3Ids = [];

  if (L2CategoryKeyFromUrl === 'all' && isEmpty(vertical)) {
    return [];
  }

  const ignoreL3CategoryIds = context.query?.[ParamKey.IGNORE_L3_CATEGORY_IDS]
    ? (context.query?.[ParamKey.IGNORE_L3_CATEGORY_IDS] as string).split(',').map((id) => +id)
    : [];

  if (L3CatgegoryList && L3CatgegoryList?.length > 0) {
    if (!isEmpty(vertical)) {
      if (vertical === VerticalName.PACKAGING) {
        activeL3Ids = L3CatgegoryList.filter((item) => item.vertical === vertical);
      } else if (vertical === VerticalName.SERVICES) {
        if (verticalName.startsWith('web')) {
          activeL3Ids = L3CatgegoryList.filter((item) => item.vertical === vertical && item.group.startsWith('web'));
        } else {
          activeL3Ids = L3CatgegoryList.filter((item) => item.vertical === vertical && !item.group.startsWith('web'));
        }
      } else {
        activeL3Ids = L3CatgegoryList.filter(
          (item) => item.vertical !== VerticalName.PACKAGING && item.vertical !== VerticalName.SERVICES,
        );
      }

      activeL3Ids = activeL3Ids?.map((L3Category) => L3Category.id).filter((id) => !ignoreL3CategoryIds.includes(id));
    } else {
      activeL3Ids = L3CatgegoryList?.map((L3Category) => L3Category.id).filter(
        (id) => !ignoreL3CategoryIds.includes(id),
      );
    }

    return activeL3Ids;
  }
};

// Transform selected filter value to api params => used in getCommonFilterParams function
const getSelectedFilterApiParam = (selectedValue: string, options: ISourcingFilterOption[]) => {
  const value = options.find((item) => item.value === selectedValue);
  return value && value?.apiParmas ? value?.apiParmas : {};
};

// Get common filter(region / countryCode / samplePrice / moq / averageReview / productionTime) params used in both recombee and search api
const getCommonFilterParams = (context: GetServerSidePropsContext) => {
  const region = context.query?.[ParamKey.REGION] ?? null;
  const countryCode = context.query?.[ParamKey.COUNTRY_CODE] ?? null;
  const samplePrice = context.query?.[ParamKey.SAMPLE_PRICE] ?? null;
  const moq = context.query?.[ParamKey.MOQ] ?? null;
  const averageReview = context.query?.[ParamKey.AVERAGE_REVIEW] ?? null;
  const productionTime = context.query?.[ParamKey.PRODUCTION_TIME] ?? null;

  const samplePriceParam = getSelectedFilterApiParam(samplePrice as string, SamplePriceFilterOptions);
  const moqParam = getSelectedFilterApiParam(moq as string, MoqFilterOptions);
  const averageReviewParam = getSelectedFilterApiParam(averageReview as string, AverageReviewFilterOptions);
  const productionTimeParam = getSelectedFilterApiParam(productionTime as string, ProductionTimeFilterOptions);

  const parmas = {
    region,
    countryCode,
    ...samplePriceParam,
    ...moqParam,
    ...averageReviewParam,
    ...productionTimeParam,
  };
  return parmas;
};

// Get noFiltersApplied param as a part of recombee api params => used in getRecombeeSupplierParams function
const getNoFilterAppliedParam = (context: GetServerSidePropsContext): boolean => {
  const region = context.query?.[ParamKey.REGION] ?? '';
  const countryCode = context.query?.[ParamKey.COUNTRY_CODE] ?? '';
  const samplePrice = context.query?.[ParamKey.SAMPLE_PRICE];
  const moq = context.query?.[ParamKey.MOQ];
  const averageReview = context.query?.[ParamKey.AVERAGE_REVIEW];
  const productionTime = context.query?.[ParamKey.PRODUCTION_TIME];

  return (
    isEmpty(region) &&
    isEmpty(countryCode) &&
    isEmpty(samplePrice) &&
    isEmpty(moq) &&
    isEmpty(averageReview) &&
    isEmpty(productionTime)
  );
};

// Get getShouldFilterSupplier param as a part of search api params
const getShouldFilterSupplierParam = (params): boolean => {
  const paramKeys = keys(delObjectKey(params, (v) => isNil(v) || (isArray(v) && isEmpty(v))));

  if (!isEmpty(params?.keyword) && paramKeys.length === 2 && paramKeys.includes('page')) {
    return true;
  }
  return false;
};

// Get recombee related params used in recombee api
const getRecombeeSupplierParams = (
  context: GetServerSidePropsContext,
  L2CategoryConfig: CategoryConfig,
  L3CategoryList: CatalogCategory[],
  adSupplierIds?: number[],
) => {
  const newUprankedSupplierIds = L2CategoryConfig?.newSupplierIds?.map((supplier) => supplier.supplierId) || [];
  const topRankedSupplierIds =
    L2CategoryConfig?.brandKitSuggestedSupplierIds?.map((supplier) => supplier.supplierId) || [];
  const noFiltersApplied = getNoFilterAppliedParam(context);
  const useTopRankedSupplierIds =
    L2CategoryConfig?.vertical === VerticalName.SERVICES && isEmpty(newUprankedSupplierIds) && noFiltersApplied;

  const recombeeSupplierParams = {
    categoryIds: getActiveL3Ids(context, L3CategoryList),
    topRankedSupplierIds: useTopRankedSupplierIds ? topRankedSupplierIds : null,
    newSupplierIds: noFiltersApplied ? newUprankedSupplierIds : null,
    uniqueName: L2CategoryConfig?.uniqueName ? L2CategoryConfig?.uniqueName : null,
    adSupplierIds,
    size: !isEmpty(adSupplierIds) ? SUPPLIERS_MAX_PAGE_SIZE : SUPPLIERS_PAGE_SIZE,
  };

  return recombeeSupplierParams;
};

// Get search related params used in search api
const getSearchSupplierParams = (
  context: GetServerSidePropsContext,
  L3CategoryList: CatalogCategory[],
  forceAllCategories?: boolean,
) => {
  const searchKey = context.query?.[ParamKey.SEARCH] ?? '';
  const sortBy = context.query?.[ParamKey.SORT] ?? SupplierSortKey.FEATURED;
  const pageParam = context.query?.[ParamKey.PAGE];
  const currentPage = pageParam ? +pageParam : 0;

  const supplierSerachParams = {
    keyword: searchKey,
    categoryIds: forceAllCategories ? [] : getActiveL3Ids(context, L3CategoryList),
    sorts: sortBy === SupplierSortKey.FEATURED ? null : sortBy,
    page: currentPage,
    size: SUPPLIERS_PAGE_SIZE,
  };

  return supplierSerachParams;
};

// Get recombee title used in recombee api
const getRecombeeTitle = (
  context: GetServerSidePropsContext,
  L3CategoryList: CatalogCategory[],
  L2CategoryConfig: CategoryConfig,
) => {
  // if only one L3 category is checked, use L3 name as title
  // else use L2 name as title
  const L3CategoryIds = getActiveL3Ids(context, L3CategoryList);
  return L3CategoryIds && L3CategoryIds.length === 1
    ? L3CategoryList.find((c) => c.id === L3CategoryIds[0])?.subgroup
    : L2CategoryConfig?.group;
};

// Add capability array for suppliers data, used in Supplier card to display
const addCapabilitiesForSuppliers = (
  suppliers: CatalogCompany[],
  supplierCapabilities: { [key: string]: string[] }[],
) => {
  const capabilitiesIds = keys(supplierCapabilities || {});
  for (const supplier of suppliers) {
    if (capabilitiesIds.length > 0) {
      if (supplier?.id && capabilitiesIds.includes(supplier.id.toString())) {
        supplier.capabilities = supplierCapabilities[supplier.id.toString()]?.slice(0, 3);
      }
    }
  }
  return suppliers;
};

// Get final supplier data
const handleRecombeeSupplierData = (
  adSuppliers: CatalogCompany[],
  normalSuppliers: CatalogCompany[],
  premiumSupplierIds: number[],
  standardSupplierIds: number[],
) => {
  const premiumSuppliers = adSuppliers.filter((item) => premiumSupplierIds?.includes(item.id)) || [];
  const standardSuppliers = normalSuppliers.filter((item) => standardSupplierIds?.includes(item.id)) || [];
  const selectedPremiumsSuppliers = sampleSize(premiumSuppliers, 16);
  const selectedStandardsSuppliers = sampleSize(standardSuppliers, 8);

  const finalAdSuppliers = selectedPremiumsSuppliers.concat(selectedStandardsSuppliers);
  const finalAdSupplierIds = finalAdSuppliers.map((item) => item.id);
  const finalNormalSuppliers = normalSuppliers.filter((item) => finalAdSupplierIds.includes(item.id) === false);
  const finalRes = finalAdSuppliers.concat(finalNormalSuppliers);

  return { finalRes, finalAdSupplierIds };
};

// Build an object, the result is like below
// {
//   Beauty(L1): {
//     Makeup(L2): {
//       Eyebrows(L3): {
//         vertical: 'xxx',
//         group: 'xxx',
//         subgroup: 'xxx',
//         id: 'xxx',
//       },
//       ...other L3
//     },
//     ...other L2
//   },
//   ...other L1
// }
function buildCategoriesMap(
  allL3List: CatalogCategory[],
  allEntryPointCategoryConfig: { [key: string]: CategoryConfig[] },
) {
  const allActiveCategoryArr = values(allEntryPointCategoryConfig).flat();
  const map = {};

  const insertSubcategory = (subcategory: CatalogCategory) => {
    const { vertical, group, subgroup } = subcategory;
    if (allActiveCategoryArr.find((item) => item?.group === group)) {
      if (!map[vertical]) {
        map[vertical] = {};
      }

      if (!map[vertical][group]) {
        map[vertical][group] = {};
      }

      if (!map[vertical][group][subgroup]) {
        map[vertical][group][subgroup] = {
          ...subcategory,
          L2Key: allActiveCategoryArr.find((item) => item.group === group)?.key,
        };
      }
    }
  };

  forEach(allL3List, insertSubcategory);
  return map;
}

// #endregion

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const userAgent = context.req.headers['user-agent'];
    const isMobile = isMobileByUserAgent(userAgent);
    //console.log('isMobile', isMobile);
    // #region - Get url params and declare variables
    const searchKey = context.query?.[ParamKey.SEARCH] ?? '';
    const sortBy = context.query?.[ParamKey.SORT] ?? SupplierSortKey.FEATURED;
    const L2CategoryKeyFromUrl = context.params.category as string;
    const pageParam = context.query?.[ParamKey.PAGE];
    const currentPage = pageParam ? +pageParam : 0;
    const ignoreL3CategoryIds = context.query?.[ParamKey.IGNORE_L3_CATEGORY_IDS]
      ? (context.query?.[ParamKey.IGNORE_L3_CATEGORY_IDS] as string).split(',').map((id) => +id)
      : [];

    // Used in get more recombee suppliers api
    let nextRecombeeId = '';
    // final supplier data used in displaying supplier list
    let supplierData: CatalogCompany[] = [];
    // When search and the supplierData is empty, display the supplierData is empty
    // TODO
    // let allCategorySupplierData: CatalogCompany[] = [];
    // Get capabilities from admin config
    let supplierCapabilities: { [key: string]: string[] }[] = [];
    // Get all L3 id, and the count number of it => { L3Id1: count, L3Id2: count, ... }
    let L3IdToCountMap: { [key: string]: number } = {};
    // The total count when search
    let searchTotal = 0;
    // used in autocomplete input
    let autocompletedPopularSuppliers: CatalogCompany[] = []; // it's used in autocompleted input
    let recommendedSupplierIds: number[] = [];
    // #endregion

    // #region - Get L2 & L3 relavant data used in category filter component
    // vertical: L1 | group: L2 | subgroup: L3
    // Get all subgroup(L3) category from API
    const { list: allL3List } = await getCatalogCategories({
      page: 0,
      size: 9999,
    });

    // Get category config from business setting
    const [allEntryPointCategoryConfigResponse, categoryPageResponse] = await Promise.all([
      businessSettingsDisplay(BusinessSettingConfigKey.CREATOR_HUB_HOMEPAGE_CATEGORIES_V2),
      businessSettingsDisplay(BusinessSettingConfigKey.PUBLIC_SOURCING_MP_CATEGORY_PAGES),
    ]);

    const allEntryPointCategoryConfig = allEntryPointCategoryConfigResponse?.value as {
      [key: string]: CategoryConfig[]; // includes the key which is used in url
    };
    const categoryPages = categoryPageResponse?.value as { [key: string]: PublicSourcingMarketplaceCategoryPage };

    const allActiveL1ToL2ToL3Map = buildCategoriesMap(allL3List, allEntryPointCategoryConfig);

    // Get L2 config by L2 handle
    const { category: currentL2Config, vertical: currentL2Vertical } = getMatchingCategoryAndVertical(
      L2CategoryKeyFromUrl,
      allEntryPointCategoryConfig,
    );

    if (!currentL2Config) {
      return { notFound: true };
    }

    // Get all L3 categories under the current L2
    const { list: activeL3List } = await getCatalogCategories({
      page: 0,
      size: 9999,
      vertical: currentL2Config.vertical,
      group: currentL2Config.group,
      subgroup: currentL2Config.subgroup,
    });
    // #endregion

    // #region - Get other config data
    // Get supplier capabilities
    const supplierCapabilitiesResponse = await businessSettingsDisplay(
      BusinessSettingConfigKey.SOURCING_CAPABILITIES_SHOWCASE,
    );
    supplierCapabilities = supplierCapabilitiesResponse?.value ?? [];
    // #endregion

    // #region - Handle supplier data used for displaying supplier list
    // Decide to use recombee or search api
    const isRecombee = isEmpty(searchKey) && sortBy === SupplierSortKey.FEATURED;

    if (isRecombee) {
      // Get premiums and standard supplier id from suppliers from config
      const [premiumSupplierResponse, standardSupplierResponse] = await Promise.all([
        businessSettingsDisplay(BusinessSettingConfigKey.PROMOTED_SUPPLIERS_PREMIUM),
        businessSettingsDisplay(BusinessSettingConfigKey.PROMOTED_SUPPLIERS_STANDARD),
      ]);

      const premiumSupplierIds = premiumSupplierResponse?.value ?? [];
      const standardSupplierIds = standardSupplierResponse?.value ?? [];

      // Get advertised supplier ids
      const adSupplierIds = premiumSupplierIds.concat(standardSupplierIds);

      // Get advertised and normal recombee supplier data
      const recombeeTitle = getRecombeeTitle(context, activeL3List, currentL2Config);
      const adParmas = {
        ...getCommonFilterParams(context),
        ...getRecombeeSupplierParams(context, currentL2Config, activeL3List, adSupplierIds),
        title: recombeeTitle,
      };
      const normalParams = {
        ...getCommonFilterParams(context),
        ...getRecombeeSupplierParams(context, currentL2Config, activeL3List),
        title: recombeeTitle,
      };
      const [adData, normalData] = await Promise.all([
        supplierRecommendItemsToUser(adParmas),
        supplierRecommendItemsToUser(normalParams),
      ]);

      // The final supplier list including 2 part of data: advertised recombee suppliers first, then the normal recombee suppliers
      const res = handleRecombeeSupplierData(
        adData?.list ?? [],
        normalData?.list ?? [],
        premiumSupplierIds,
        standardSupplierIds,
      );
      supplierData = res.finalRes;
      recommendedSupplierIds = res.finalAdSupplierIds;
      supplierData = addCapabilitiesForSuppliers(supplierData, supplierCapabilities);

      nextRecombeeId = normalData?.recommId;
    } else {
      // There are 2 part of data: filter suppliers by category ids, and all categories suppliers used to display when filter suppliers data is empty
      const filterSupplierParams = {
        ...getCommonFilterParams(context),
        ...getSearchSupplierParams(context, activeL3List),
      };
      if (getShouldFilterSupplierParam(filterSupplierParams)) {
        filterSupplierParams.shouldFilterSupplier = true;
      }

      const allSupplierParams = {
        ...getCommonFilterParams(context),
        ...getSearchSupplierParams(context, activeL3List, true),
      };
      if (getShouldFilterSupplierParam(allSupplierParams)) {
        allSupplierParams.shouldFilterSupplier = true;
      }

      const [filterSupplierData, allSupplierData] = await Promise.all([
        getCatalogCompanies(filterSupplierParams),
        getCatalogCompanies(allSupplierParams),
      ]);

      supplierData = addCapabilitiesForSuppliers(filterSupplierData?.list ?? [], supplierCapabilities);
      // allCategorySupplierData = addCapabilitiesForSuppliers(allSupplierData?.list ?? [], supplierCapabilities);
      searchTotal = filterSupplierData?.total || 0;

      // need to order L2 & L3 category by the total count accroding to the searchKey
      if (!isEmpty(searchKey)) {
        L3IdToCountMap = await categoryCountBySuppliersInSearch({
          keyword: searchKey as string,
          groupField: 'category_id',
        });
      }
    }
    // #endregion

    // #region - Get default popular suppliers used in autocompleted input
    const topSupplierIds = currentL2Config?.brandKitSuggestedSupplierIds?.map((s) => s.supplierId) || [];
    if (!searchKey && topSupplierIds.length) {
      const { list } = await getCatalogCompaniesByIds(topSupplierIds);
      autocompletedPopularSuppliers = list || [];
    }
    // #endregion

    context.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300');
    return {
      props: {
        category: currentL2Config,
        vertical: currentL2Vertical,
        categoryConfig: allEntryPointCategoryConfig,
        categoryPage: categoryPages[context.resolvedUrl] ?? null,
        suppliers: supplierData,
        allCategorySuppliers: [], // TODO: allCategorySupplierData,
        // allCategorySuppliers: allCategorySupplierData,
        page: currentPage,
        useRecommendationBackend: isRecombee,
        recombeeId: nextRecombeeId,
        total: searchTotal,
        defaultPopularSuppliers: autocompletedPopularSuppliers,
        searchKey,
        allL1ToL2ToL3Map: allActiveL1ToL2ToL3Map,
        ignoreL3CategoryIds,
        L3IdToCountMap,
        recommendedSupplierIds,
        isMobile,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

const getPageTitle = (
  category: CategoryConfig,
  vertical: VerticalName,
  categoryPage?: PublicSourcingMarketplaceCategoryPage,
  searchKey?: string | string[],
) => {
  if (categoryPage && !searchKey) {
    return categoryPage.metadataTitle;
  }

  const searchKeyText = Array.isArray(searchKey) ? searchKey.join(' ') : searchKey;

  if (searchKeyText) {
    return `The best ${searchKeyText} ${getVerticalName(vertical)} for e-commerce | Pietra`;
  }

  const currentYear = new Date().getFullYear();
  return `The 15 best ${category?.title} ${getVerticalName(vertical)} in ${currentYear} | Pietra`;
};

const getPageDescription = (
  category: CategoryConfig,
  vertical: VerticalName,
  categoryPage?: PublicSourcingMarketplaceCategoryPage,
  searchKey?: string | string[],
) => {
  if (categoryPage && !searchKey) {
    return categoryPage.metadataDescription;
  }

  const searchKeyText = Array.isArray(searchKey) ? searchKey.join(' ') : searchKey;

  if (searchKeyText) {
    return `Discover top-tier ${searchKeyText} manufacturers providing high-quality products and customization options. Ideal suppliers to elevate your e-commerce brand.`;
  }

  return `Browse ${getVerticalName(vertical)} to work with for your ${category?.title} brand.`;
};

const getPageImage = (category: CategoryConfig, categoryPage?: PublicSourcingMarketplaceCategoryPage) => {
  const image = categoryPage?.metadataImage || category.image;
  return cloudfrontLoader({ src: image, quality: 90, width: 300 });
};

const BrowseSuppliersPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = ({
  category,
  vertical,
  suppliers,
  page,
  total,
  recombeeId,
  useRecommendationBackend,
  categoryPage,
  searchKey,
  allCategorySuppliers,
  recommendedSupplierIds,
  isMobile,
}) => {
  //console.log('isMobile from server:', isMobile);
  useEffect(() => {
    if (category?.title) {
      logPageView({
        pageUrl: window.location.href || '',
        routeName: window.location.pathname,
        pageTitle: getPageTitle(category, vertical, categoryPage, searchKey),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.title]);
  return (
    <BrowseSupplierList
      isMobile={isMobile}
      category={category}
      vertical={vertical}
      suppliers={suppliers}
      page={page}
      total={total}
      recombeeId={recombeeId}
      useRecommendationBackend={useRecommendationBackend}
      recommendedSupplierIds={recommendedSupplierIds}
      // allCategorySuppliers={allCategorySuppliers}
    />
  );
};

BrowseSuppliersPage.getLayout = function getLayout(page: React.ReactElement) {
  const {
    isMobile,
    category,
    vertical,
    defaultPopularSuppliers,
    categoryConfig,
    categoryPage,
    searchKey,
    allL1ToL2ToL3Map,
    ignoreL3CategoryIds,
    L3IdToCountMap,
  } = page.props;
  const description = getPageDescription(category, vertical, categoryPage, searchKey);

  return (
    <>
      <CustomHead
        title={getPageTitle(category, vertical, void 0, searchKey)}
        description={description}
        url={`${WEBSITE_URL}/sourcing/browse/${category.key}/suppliers`}
        image={getPageImage(category, categoryPage)}
      />
      <Layout hideFooter>
        <BrowseSourcingLayout
          isMobile={isMobile}
          category={category}
          vertical={vertical}
          viewType={ViewType.SUPPLIERS}
          categoryList={[]}
          searchCounts={{}}
          categoryConfig={categoryConfig}
          defaultPopularSuppliers={defaultPopularSuppliers}
          categoryPage={categoryPage}
          allL1ToL2ToL3Map={allL1ToL2ToL3Map}
          ignoreL3CategoryIds={ignoreL3CategoryIds}
          L3IdToCountMap={L3IdToCountMap}
        >
          {page}
        </BrowseSourcingLayout>
      </Layout>
      <Script
        id="product-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            getBreadcrumbListJsonLd([{ id: `${WEBSITE_URL}/sourcing`, name: 'Browse all categories' }]),
          ]),
        }}
      />
    </>
  );
};

export default BrowseSuppliersPage;
